import { Button } from "@/shared/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { useEffect, useMemo, useState } from "react";
import { UNIVERSAL_THROTTLING_METHODS } from "../hooks/useUniversalThrottlingPolicies";
import { CHANNELS } from "@/constants/channels";
import { toast } from "@/shared/hooks/useToast";
import type {
    UniversalThrottlingChannel,
    UniversalThrottlingMethod,
    UniversalThrottlingMethodName,
    UniversalThrottlingPolicy,
} from "../types/preferences_types";

function parseValue(raw: string): number {
    const trimmed = raw.trim();
    if (!trimmed) return NaN;
    
    return Number.parseFloat(trimmed);
}

function validateMethods(methods: UniversalThrottlingMethod[]): string | null {
    if (methods.length === 0) return "최소 하나의 스로틀링 메서드가 필요합니다.";

    const keys = new Set(methods.map((m) => m.method));
    const allowed =
        (keys.size === 1 && keys.has("sleep_sec")) ||
        (keys.size === 2 && keys.has("max_concurrency") && keys.has("max_run_time_sec"));

    if (!allowed) return "지원되지 않는 스로틀링 메서드 조합입니다. (sleep_sec 단독 또는 max_concurrency + max_run_time_sec 조합만 가능)";

    for (const m of methods) {
        if (!Number.isFinite(m.value)) return `${m.method}는 유효한 숫자 값이어야 합니다.`;
        
        // 백엔드 검증 규칙과 일치: max_concurrency는 >= 1, 나머지는 >= 0
        if (m.method === "max_concurrency") {
            if (!Number.isInteger(m.value)) return `${m.method}는 정수 값이어야 합니다.`;
            if (m.value < 1) return `${m.method}는 1 이상이어야 합니다.`;
        } else if (m.method === "sleep_sec" || m.method === "max_run_time_sec") {
            if (m.value < 0) return `${m.method}는 0 이상이어야 합니다.`;
        }
    }
    return null;
}

export function UniversalThrottlingPolicyDialog({
    open,
    onOpenChange,
    mode,
    initial,
    onSubmit,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: "create" | "edit";
    initial?: UniversalThrottlingPolicy;
    onSubmit: (payload: {
        channel: UniversalThrottlingChannel;
        throttlingKey: string;
        throttlingMethod: UniversalThrottlingMethod[];
    }) => void;
}) {
    const title = useMemo(() => (mode === "create" ? "스로틀링 정책 추가" : "스로틀링 정책 수정"), [mode]);

    const [channel, setChannel] = useState<UniversalThrottlingChannel>(CHANNELS[0]);
    const [throttlingKey, setThrottlingKey] = useState("");
    const [methods, setMethods] = useState<Array<{ method: UniversalThrottlingMethodName; value: string }>>([]);

    useEffect(() => {
        if (!open) return;

        if (mode === "edit" && initial) {
            setChannel(initial.channel);
            setThrottlingKey(initial.throttlingKey);
            setMethods(initial.throttlingMethod.map((m) => ({ method: m.method, value: String(m.value) })));
            return;
        }

        setChannel(CHANNELS[0]);
        setThrottlingKey("");
        setMethods([]);
    }, [open, mode, initial]);

    const addMethod = () => {
        setMethods((prev) => [...prev, { method: "sleep_sec", value: "" }]);
    };

    const removeMethod = (index: number) => {
        setMethods((prev) => prev.filter((_, i) => i !== index));
    };

    const updateMethod = (index: number, next: UniversalThrottlingMethodName) => {
        setMethods((prev) => prev.map((m, i) => (i === index ? { ...m, method: next } : m)));
    };

    const updateValue = (index: number, next: string) => {
        setMethods((prev) => prev.map((m, i) => (i === index ? { ...m, value: next } : m)));
    };

    // 유효성 검증: 모든 메서드의 값이 입력되었는지 확인
    const canSubmit = 
        throttlingKey.trim().length > 0 && 
        methods.length > 0 && 
        methods.every((m) => {
            const trimmed = m.value.trim();
            if (!trimmed) return false;
            const parsed = parseValue(trimmed);
            if (!Number.isFinite(parsed)) return false;
            
            if (m.method === "max_concurrency") {
                return Number.isInteger(parsed) && parsed >= 1;
            }
            return parsed >= 0;
        });

    const handleSubmit = () => {
        // 기본 검증
        if (!throttlingKey.trim()) {
            toast({
                title: "입력 오류",
                description: "스로틀링 키를 입력해주세요.",
                variant: "destructive",
            });
            return;
        }

        if (methods.length === 0) {
            toast({
                title: "입력 오류",
                description: "최소 하나의 스로틀링 메서드를 추가해주세요.",
                variant: "destructive",
            });
            return;
        }

        // 값 파싱
        const parsedMethods: UniversalThrottlingMethod[] = methods.map((m) => ({
            method: m.method,
            value: parseValue(m.value.trim()),
        }));

        // 상세 유효성 검증
        const validationError = validateMethods(parsedMethods);
        if (validationError) {
            toast({
                title: "유효성 검증 실패",
                description: validationError,
                variant: "destructive",
            });
            return;
        }

        // 제출
        onSubmit({
            channel,
            throttlingKey: throttlingKey.trim(),
            throttlingMethod: parsedMethods,
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <div className="text-sm font-medium">채널</div>
                            <Select value={channel} onValueChange={(v) => setChannel(v as UniversalThrottlingChannel)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CHANNELS.map((c) => (
                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <div className="text-sm font-medium">스로틀링 키</div>
                            <Input
                                value={throttlingKey}
                                onChange={(e) => setThrottlingKey(e.target.value)}
                                maxLength={1024}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="text-sm font-medium">스로틀링 메서드</div>
                            <Button type="button" variant="ghost" onClick={addMethod}>
                                + 추가
                            </Button>
                        </div>

                        <div className="space-y-2">
                            {methods.map((m, idx) => (
                                <div key={idx} className="flex flex-wrap items-center gap-2">
                                    <div className="w-[220px]">
                                        <Select value={m.method} onValueChange={(v) => updateMethod(idx, v as UniversalThrottlingMethodName)}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {UNIVERSAL_THROTTLING_METHODS.map((name) => (
                                                    <SelectItem key={name} value={name}>{name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <Input
                                        type="number"
                                        step={m.method === "max_concurrency" ? "1" : "0.01"}
                                        min="0"
                                        value={m.value}
                                        onChange={(e) => updateValue(idx, e.target.value)}
                                        placeholder="값을 입력하세요"
                                        className="w-[160px]"
                                    />

                                    <Button type="button" variant="ghost" onClick={() => removeMethod(idx)}>
                                        X
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>취소</Button>
                    <Button
                        disabled={!canSubmit}
                        onClick={handleSubmit}
                    >
                        {mode === "create" ? "추가" : "수정"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
